import { createElement } from "react";
import {
  ChevronDown, ChevronRight, User, Building2,
} from "lucide-react";

const TONE_STYLES = {
  completo:  { ring: "ring-emerald-300", chip: "bg-emerald-100 text-emerald-700 border-emerald-200", dot: "bg-emerald-500", label: "Completo" },
  excedido:  { ring: "ring-sky-300",     chip: "bg-sky-100 text-sky-700 border-sky-200",             dot: "bg-sky-500",     label: "Acima" },
  atencao:   { ring: "ring-amber-300",   chip: "bg-amber-100 text-amber-800 border-amber-200",       dot: "bg-amber-500",   label: "Atenção" },
  deficit:   { ring: "ring-rose-300",    chip: "bg-rose-100 text-rose-700 border-rose-200",          dot: "bg-rose-500",    label: "Abaixo" },
  neutral:   { ring: "ring-slate-200",   chip: "bg-slate-100 text-slate-600 border-slate-200",       dot: "bg-slate-400",   label: "Sem meta" },
};

const LEVEL_GRADIENTS = [
  "from-[#234935] to-[#1A3528]",
  "from-[#2c5c43] to-[#234935]",
  "from-[#3a7559] to-[#2c5c43]",
  "from-[#5a9477] to-[#3a7559]",
  "from-[#83b89c] to-[#5a9477]",
];

export default function OrgNode({ node, collapsed, onToggle, showResp, showCounts }) {
  const isCollapsed = !!collapsed[node.id];
  const hasChildren = (node.children || []).length > 0;
  const tone = TONE_STYLES[node.tone] || TONE_STYLES.neutral;
  const grad = LEVEL_GRADIENTS[Math.min(node.level, LEVEL_GRADIENTS.length - 1)];

  const diffCls =
    node.diferenca === 0
      ? ""
      : node.diferenca < 0
      ? "text-rose-200 print:text-rose-600"
      : "text-sky-200 print:text-sky-600";

  return (
    <div className="flex flex-col items-center">
      <div
        className={`relative w-[230px] rounded-xl bg-gradient-to-br ${grad} text-white shadow-lg ring-1 ${tone.ring} hover:shadow-xl transition-shadow print:shadow-none print:ring-1 print:ring-slate-300 print:bg-white print:text-slate-900`}
        data-testid={`org-node-${node.id}`}
      >
        <div className={`absolute inset-x-0 top-0 h-1 rounded-t-xl ${tone.dot}`} />
        <div className="p-3.5 pt-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-white/15 print:bg-slate-100 px-1.5 py-0.5 text-[9.5px] uppercase tracking-[0.2em] font-medium">
              <Building2 className="h-2.5 w-2.5" /> Nível {node.level}
            </span>
            <span className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[9.5px] uppercase tracking-[0.16em] ${tone.chip}`}>
              {tone.label}
            </span>
          </div>
          <div className="font-display font-semibold text-[14.5px] leading-snug tracking-tight line-clamp-2" data-testid={`org-node-name-${node.id}`}>
            {node.name}
          </div>
          {showResp && node.responsavel && (
            <div className="mt-2 flex items-center gap-1.5 text-[11px] text-white/80 print:text-slate-600">
              <User className="h-3 w-3" />
              <span className="truncate">{node.responsavel}</span>
            </div>
          )}
          {showCounts && (
            <div className="mt-2.5 grid grid-cols-3 gap-1.5 text-center">
              <div className="rounded bg-white/10 print:bg-slate-100 py-1">
                <div className="text-[9px] uppercase tracking-wider opacity-70">Atual</div>
                <div className="font-display text-[14px] font-semibold tabular-nums">{node.quadro_atual}</div>
              </div>
              <div className="rounded bg-white/10 print:bg-slate-100 py-1">
                <div className="text-[9px] uppercase tracking-wider opacity-70">Aprov.</div>
                <div className="font-display text-[14px] font-semibold tabular-nums">{node.quadro_aprovado}</div>
              </div>
              <div className="rounded bg-white/10 print:bg-slate-100 py-1">
                <div className="text-[9px] uppercase tracking-wider opacity-70">Dif.</div>
                <div className={`font-display text-[14px] font-semibold tabular-nums ${diffCls}`}>
                  {node.diferenca >= 0 ? `+${node.diferenca}` : node.diferenca}
                </div>
              </div>
            </div>
          )}
          {hasChildren && (
            <button
              onClick={() => onToggle(node.id)}
              className="absolute -bottom-3 left-1/2 -translate-x-1/2 h-6 w-6 rounded-full bg-[#C5A059] text-[#1A231F] grid place-items-center shadow-md hover:bg-[#D4B36B] transition-colors print:hidden"
              data-testid={`org-toggle-${node.id}`}
              title={isCollapsed ? "Expandir" : "Recolher"}
            >
              {isCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
          )}
        </div>
      </div>

      {hasChildren && !isCollapsed && (
        <div className="flex flex-col items-center w-full">
          <div className="w-px h-8 bg-slate-300 print:bg-slate-400" />
          <div className="flex items-start gap-6 print:gap-3 relative">
            {node.children.map((c) =>
              createElement("div", {
                key: c.id,
                className: "relative flex flex-col items-center",
              },
                createElement("div", { className: "w-px h-4 bg-slate-300 print:bg-slate-400" }),
                createElement(OrgNode, {
                  node: c,
                  collapsed,
                  onToggle,
                  showResp,
                  showCounts,
                })
              )
            )}
            {node.children.length > 1 && (
              <div className="absolute top-0 left-[7.2rem] right-[7.2rem] h-px bg-slate-300 print:bg-slate-400" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
